import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { loginRequest, isValidAdminUser } from '../authConfig';
import {
  Container,
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { Login as LoginIcon } from '@mui/icons-material';

export default function Login() {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    console.log('Login page - Auth state:', { isAuthenticated, accountsCount: accounts.length, inProgress });
    
    // Wait for authentication to complete
    if (inProgress === 'none' && isAuthenticated && accounts.length > 0) {
      const account = accounts[0];
      console.log('Checking account:', account.username, account.tenantId);
      
      if (isValidAdminUser(account.username, account.tenantId)) {
        console.log('Valid admin user, navigating to dashboard');
        navigate('/dashboard', { replace: true });
      } else {
        setError('Access denied. Admin portal requires a tc-config.com or technologyconsulting.llc email address.');
        instance.logoutRedirect();
      }
    }
  }, [isAuthenticated, accounts, navigate, instance, inProgress]);

  const handleLogin = async () => {
    setError('');
    setLoading(true);

    try {
      await instance.loginRedirect(loginRequest);
    } catch (err: any) {
      console.error('Login error:', err);
      setError('An error occurred during login. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}
    >
      <Container maxWidth="sm">
        <Card elevation={8}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <LoginIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h4" component="h1" gutterBottom>
                Admin Portal
              </Typography>
              <Typography variant="body2" color="text.secondary">
                TC Config
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
              Sign in with your tc-config.com or technologyconsulting.llc account
            </Typography>

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleLogin}
              disabled={loading}
              sx={{ mt: 2, mb: 2, py: 1.5 }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Redirecting to Microsoft...
                </>
              ) : (
                'Sign in with Microsoft'
              )}
            </Button>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
